import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Progress } from '@/components/ui/progress';
import { 
  FileText, Shield, Users, FolderOpen, Plus, Edit, Trash2, 
  ChevronDown, ChevronRight, Upload, Check, X, AlertTriangle,
  ExternalLink, Download, CheckCircle, Clock, FileCheck, Send,
  ThumbsUp, ThumbsDown, Link2, RefreshCw, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DPIASection = () => {
  const [dpias, setDpias] = useState([]);
  const [marketingActions, setMarketingActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDpia, setEditingDpia] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    marketing_action_id: '',
    risk_level: 'medium',
    data_types: '',
    processing_purposes: '',
    risk_assessment: '',
    mitigation_measures: '',
    dpo_opinion: '',
    status: 'draft'
  });

  useEffect(() => {
    fetchDpias();
    fetchMarketingActions();
  }, []);

  const fetchDpias = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/compliance/dpia`, { withCredentials: true });
      setDpias(response.data);
    } catch (error) {
      toast.error('Erro ao carregar DPIAs');
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketingActions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/compliance/marketing-actions-list`, { withCredentials: true });
      setMarketingActions(response.data);
    } catch (error) {
      console.error('Error fetching marketing actions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        marketing_action_id: form.marketing_action_id || null,
        data_types: form.data_types.split(',').map(s => s.trim()).filter(Boolean),
        processing_purposes: form.processing_purposes.split(',').map(s => s.trim()).filter(Boolean)
      };

      if (editingDpia) {
        await axios.put(`${BACKEND_URL}/api/compliance/dpia/${editingDpia.dpia_id}`, payload, { withCredentials: true });
        toast.success('DPIA atualizado!');
      } else {
        await axios.post(`${BACKEND_URL}/api/compliance/dpia`, payload, { withCredentials: true });
        toast.success('DPIA criado!');
      }
      setDialogOpen(false);
      resetForm();
      fetchDpias();
    } catch (error) {
      toast.error('Erro ao guardar DPIA');
    }
  };

  const handleDelete = async (dpiaId) => {
    if (!window.confirm('Tem certeza que deseja eliminar este DPIA?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/compliance/dpia/${dpiaId}`, { withCredentials: true });
      toast.success('DPIA eliminado!');
      fetchDpias();
    } catch (error) {
      toast.error('Erro ao eliminar DPIA');
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      marketing_action_id: '',
      risk_level: 'medium',
      data_types: '',
      processing_purposes: '',
      risk_assessment: '',
      mitigation_measures: '',
      dpo_opinion: '',
      status: 'draft'
    });
    setEditingDpia(null);
  };

  const openEdit = (dpia) => {
    setEditingDpia(dpia);
    setForm({
      name: dpia.name,
      description: dpia.description || '',
      marketing_action_id: dpia.marketing_action_id || '',
      risk_level: dpia.risk_level,
      data_types: (dpia.data_types || []).join(', '),
      processing_purposes: (dpia.processing_purposes || []).join(', '),
      risk_assessment: dpia.risk_assessment || '',
      mitigation_measures: dpia.mitigation_measures || '',
      dpo_opinion: dpia.dpo_opinion || '',
      status: dpia.status
    });
    setDialogOpen(true);
  };

  const getRiskBadge = (level) => {
    const colors = {
      low: 'bg-emerald-50 text-emerald-700',
      medium: 'bg-amber-50 text-amber-700',
      high: 'bg-orange-50 text-orange-700',
      critical: 'bg-red-50 text-red-700'
    };
    return <Badge className={colors[level] || 'bg-slate-50'}>{level}</Badge>;
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-slate-50 text-slate-700',
      pending_review: 'bg-amber-50 text-amber-700',
      approved: 'bg-emerald-50 text-emerald-700',
      rejected: 'bg-red-50 text-red-700'
    };
    const labels = {
      draft: 'Rascunho',
      pending_review: 'Em Revisão',
      approved: 'Aprovado',
      rejected: 'Rejeitado'
    };
    return <Badge className={colors[status] || 'bg-slate-50'}>{labels[status] || status}</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-lg">Avaliações de Impacto (DPIA)</h3>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="w-4 h-4 mr-2" />
              Novo DPIA
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingDpia ? 'Editar DPIA' : 'Novo DPIA'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Nome *</Label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
                </div>
                <div className="col-span-2">
                  <Label>Ação de Marketing Associada</Label>
                  <Select value={form.marketing_action_id || 'none'} onValueChange={v => setForm({...form, marketing_action_id: v === 'none' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Selecionar ação de marketing (opcional)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhuma</SelectItem>
                      {marketingActions.map(action => (
                        <SelectItem key={action.action_id} value={action.action_id}>
                          {action.name} ({action.status})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Descrição</Label>
                  <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
                </div>
                <div>
                  <Label>Nível de Risco</Label>
                  <Select value={form.risk_level} onValueChange={v => setForm({...form, risk_level: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Baixo</SelectItem>
                      <SelectItem value="medium">Médio</SelectItem>
                      <SelectItem value="high">Alto</SelectItem>
                      <SelectItem value="critical">Crítico</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Estado</Label>
                  <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Rascunho</SelectItem>
                      <SelectItem value="pending_review">Em Revisão</SelectItem>
                      <SelectItem value="approved">Aprovado</SelectItem>
                      <SelectItem value="rejected">Rejeitado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Tipos de Dados (separados por vírgula)</Label>
                  <Input value={form.data_types} onChange={e => setForm({...form, data_types: e.target.value})} placeholder="Nome, Email, Telefone..." />
                </div>
                <div className="col-span-2">
                  <Label>Finalidades do Tratamento (separadas por vírgula)</Label>
                  <Input value={form.processing_purposes} onChange={e => setForm({...form, processing_purposes: e.target.value})} placeholder="Marketing, Análise, Comunicação..." />
                </div>
                <div className="col-span-2">
                  <Label>Avaliação de Risco</Label>
                  <Textarea value={form.risk_assessment} onChange={e => setForm({...form, risk_assessment: e.target.value})} rows={3} />
                </div>
                <div className="col-span-2">
                  <Label>Medidas de Mitigação</Label>
                  <Textarea value={form.mitigation_measures} onChange={e => setForm({...form, mitigation_measures: e.target.value})} rows={3} />
                </div>
                <div className="col-span-2">
                  <Label>Parecer do DPO</Label>
                  <Textarea value={form.dpo_opinion} onChange={e => setForm({...form, dpo_opinion: e.target.value})} rows={2} placeholder="Parecer técnico do Encarregado de Proteção de Dados..." />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                <Button type="submit">Guardar</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-500">A carregar...</div>
      ) : dpias.length === 0 ? (
        <div className="text-center py-8 text-slate-500">Nenhum DPIA registado</div>
      ) : (
        <div className="space-y-3">
          {dpias.map(dpia => {
            const linkedAction = marketingActions.find(a => a.action_id === dpia.marketing_action_id);
            return (
            <Card key={dpia.dpia_id} className="border-slate-200">
              <CardContent className="pt-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-[#0F172A]">{dpia.name}</h4>
                      {getRiskBadge(dpia.risk_level)}
                      {getStatusBadge(dpia.status)}
                    </div>
                    {linkedAction && (
                      <div className="flex items-center gap-1 text-xs text-blue-600 mb-2">
                        <Link2 className="w-3 h-3" />
                        <span>Associado a: {linkedAction.name}</span>
                      </div>
                    )}
                    {dpia.description && <p className="text-sm text-slate-600 mb-2">{dpia.description}</p>}
                    <div className="text-xs text-slate-500">
                      Criado em {new Date(dpia.created_at).toLocaleDateString('pt-PT')} por {dpia.created_by_name}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(dpia)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(dpia.dpia_id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )})}
        </div>
      )}
    </div>
  );
};

const ChecklistSection = () => {
  const [items, setItems] = useState([]);
  const [marketingActions, setMarketingActions] = useState([]);
  const [selectedAction, setSelectedAction] = useState('');
  const [workflowData, setWorkflowData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', category: '', order: 0, is_mandatory: true });
  const [activeTab, setActiveTab] = useState('items'); // 'items' | 'workflow'

  useEffect(() => {
    fetchItems();
    fetchMarketingActions();
  }, []);

  useEffect(() => {
    if (selectedAction) {
      fetchWorkflow();
    } else {
      setWorkflowData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAction]);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/compliance/checklist-items`, { withCredentials: true });
      setItems(response.data);
    } catch (error) {
      toast.error('Erro ao carregar itens');
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketingActions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/compliance/marketing-actions-list`, { withCredentials: true });
      setMarketingActions(response.data);
    } catch (error) {
      console.error('Error fetching marketing actions:', error);
    }
  };

  const fetchWorkflow = async () => {
    if (!selectedAction) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/compliance/checklist-workflow/${selectedAction}`, { withCredentials: true });
      setWorkflowData(response.data);
    } catch (error) {
      console.error('Error fetching workflow:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/compliance/checklist-items/${editingItem.item_id}`, form, { withCredentials: true });
        toast.success('Item atualizado!');
      } else {
        await axios.post(`${BACKEND_URL}/api/compliance/checklist-items`, form, { withCredentials: true });
        toast.success('Item criado!');
      }
      setDialogOpen(false);
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error('Erro ao guardar item');
    }
  };

  const resetForm = () => {
    setForm({ name: '', description: '', category: '', order: 0, is_mandatory: true });
    setEditingItem(null);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name,
      description: item.description || '',
      category: item.category || '',
      order: item.order || 0,
      is_mandatory: item.is_mandatory !== false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Eliminar este item?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/compliance/checklist-items/${itemId}`, { withCredentials: true });
      toast.success('Item eliminado!');
      fetchItems();
    } catch (error) {
      toast.error('Erro ao eliminar item');
    }
  };

  const handleToggleValidation = async (item, isCompleted) => {
    if (!selectedAction) return;
    try {
      await axios.post(`${BACKEND_URL}/api/compliance/checklist-validations`, {
        marketing_action_id: selectedAction,
        checklist_item_id: item.item_id,
        is_completed: isCompleted,
        notes: item.notes || ''
      }, { withCredentials: true });
      fetchWorkflow();
    } catch (error) {
      toast.error('Erro ao atualizar validação');
    }
  };

  const handleSubmitForApproval = async () => {
    if (!selectedAction) return;
    try {
      await axios.post(`${BACKEND_URL}/api/compliance/checklist-workflow/submit`, {
        marketing_action_id: selectedAction,
        status: 'in_review'
      }, { withCredentials: true });
      toast.success('Checklist enviado para aprovação do DPO!');
      fetchWorkflow();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao submeter');
    }
  };

  const handleApprove = async (approved) => {
    if (!selectedAction) return;
    try {
      await axios.post(`${BACKEND_URL}/api/compliance/checklist-workflow/approve?marketing_action_id=${selectedAction}&approved=${approved}`, {}, { withCredentials: true });
      toast.success(approved ? 'Checklist aprovado!' : 'Checklist rejeitado');
      fetchWorkflow();
    } catch (error) {
      toast.error('Erro ao processar aprovação');
    }
  };

  const getWorkflowStatusBadge = (status) => {
    const colors = {
      pending: 'bg-slate-100 text-slate-700',
      in_review: 'bg-amber-50 text-amber-700',
      approved: 'bg-emerald-50 text-emerald-700',
      rejected: 'bg-red-50 text-red-700'
    };
    const labels = {
      pending: 'Pendente',
      in_review: 'Em Revisão DPO',
      approved: 'Aprovado',
      rejected: 'Rejeitado'
    };
    return <Badge className={colors[status] || 'bg-slate-100'}>{labels[status] || status}</Badge>;
  };

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="items">Itens de Checklist</TabsTrigger>
            <TabsTrigger value="workflow">Workflow por Ação</TabsTrigger>
          </TabsList>
          
          {activeTab === 'items' && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="new-checklist-item-btn"><Plus className="w-4 h-4 mr-2" />Novo Item</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{editingItem ? 'Editar Item' : 'Novo Item de Checklist'}</DialogTitle></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Nome *</Label>
                    <Input data-testid="checklist-item-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Descrição</Label>
                    <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
                  </div>
                  <div>
                    <Label>Categoria</Label>
                    <Input value={form.category} onChange={e => setForm({...form, category: e.target.value})} placeholder="Ex: Legal, Técnico, Organizacional" />
                  </div>
                  <div>
                    <Label>Ordem</Label>
                    <Input type="number" value={form.order} onChange={e => setForm({...form, order: parseInt(e.target.value) || 0})} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={form.is_mandatory} onCheckedChange={v => setForm({...form, is_mandatory: v})} />
                    <Label>Obrigatório</Label>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancelar</Button>
                    <Button type="submit" data-testid="save-checklist-item-btn">Guardar</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <TabsContent value="items">
          {loading ? (
            <div className="text-center py-8 text-slate-500">A carregar...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-slate-500">Nenhum item de checklist definido</div>
          ) : (
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={item.item_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border" data-testid={`checklist-item-${item.item_id}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center text-xs font-medium">{idx + 1}</span>
                    <div>
                      <div className="font-medium text-[#0F172A]">{item.name}</div>
                      {item.description && <div className="text-xs text-slate-500">{item.description}</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.is_mandatory && <Badge variant="outline" className="text-xs">Obrigatório</Badge>}
                    {item.category && <Badge variant="outline" className="text-xs">{item.category}</Badge>}
                    <Button variant="ghost" size="sm" onClick={() => openEdit(item)}>
                      <Edit className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item.item_id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="workflow">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label>Selecionar Ação de Marketing</Label>
                <Select value={selectedAction} onValueChange={setSelectedAction}>
                  <SelectTrigger data-testid="workflow-action-select"><SelectValue placeholder="Selecionar ação..." /></SelectTrigger>
                  <SelectContent>
                    {marketingActions.map(action => (
                      <SelectItem key={action.action_id} value={action.action_id}>
                        {action.name} ({action.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedAction && workflowData && (
              <div className="space-y-4">
                {/* Workflow Status Header */}
                <Card className="border-slate-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div>
                          <div className="text-sm text-slate-500">Estado do Workflow</div>
                          <div className="mt-1">{getWorkflowStatusBadge(workflowData.workflow?.status || 'pending')}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-500">Progresso</div>
                          <div className="flex items-center gap-2 mt-1">
                            <Progress value={workflowData.stats?.completion_percentage || 0} className="w-32" />
                            <span className="text-sm font-medium">{workflowData.stats?.completion_percentage || 0}%</span>
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-500">Obrigatórios</div>
                          <div className="text-sm font-medium mt-1">
                            {workflowData.stats?.completed_mandatory || 0} / {workflowData.stats?.total_mandatory || 0}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {workflowData.workflow?.status === 'pending' && workflowData.stats?.can_submit_for_approval && (
                          <Button onClick={handleSubmitForApproval} className="bg-blue-600 hover:bg-blue-700">
                            <Send className="w-4 h-4 mr-2" />Submeter para Aprovação
                          </Button>
                        )}
                        {workflowData.workflow?.status === 'in_review' && (
                          <>
                            <Button variant="outline" onClick={() => handleApprove(false)} className="text-red-600 border-red-300">
                              <ThumbsDown className="w-4 h-4 mr-2" />Rejeitar
                            </Button>
                            <Button onClick={() => handleApprove(true)} className="bg-emerald-600 hover:bg-emerald-700">
                              <ThumbsUp className="w-4 h-4 mr-2" />Aprovar
                            </Button>
                          </>
                        )}
                        {workflowData.workflow?.status === 'rejected' && (
                          <Button variant="outline" onClick={handleSubmitForApproval}>
                            <RefreshCw className="w-4 h-4 mr-2" />Resubmeter
                          </Button>
                        )}
                      </div>
                    </div>
                    {workflowData.workflow?.approved_by_name && (
                      <div className="mt-3 pt-3 border-t text-sm text-slate-500">
                        {workflowData.workflow?.status === 'approved' ? 'Aprovado' : 'Rejeitado'} por{' '}
                        <span className="font-medium">{workflowData.workflow.approved_by_name}</span>
                        {workflowData.workflow.approved_at && (
                          <> em {new Date(workflowData.workflow.approved_at).toLocaleDateString('pt-PT')}</>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Checklist Items for Validation */}
                <div className="space-y-2">
                  {(workflowData.items || []).map((item, idx) => (
                    <div 
                      key={item.item_id} 
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        item.is_completed ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleToggleValidation(item, !item.is_completed)}
                          disabled={workflowData.workflow?.status === 'approved'}
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                            item.is_completed 
                              ? 'bg-emerald-500 border-emerald-500 text-white' 
                              : 'border-slate-300 hover:border-emerald-400'
                          } ${workflowData.workflow?.status === 'approved' ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                        >
                          {item.is_completed && <Check className="w-4 h-4" />}
                        </button>
                        <div>
                          <div className={`font-medium ${item.is_completed ? 'text-emerald-800' : 'text-[#0F172A]'}`}>
                            {item.name}
                          </div>
                          {item.description && <div className="text-xs text-slate-500">{item.description}</div>}
                          {item.validated_by_name && (
                            <div className="text-xs text-emerald-600 mt-1">
                              Validado por {item.validated_by_name}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {item.is_mandatory && <Badge variant="outline" className="text-xs">Obrigatório</Badge>}
                        {item.category && <Badge variant="outline" className="text-xs">{item.category}</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedAction && !workflowData && (
              <div className="text-center py-8 text-slate-500">A carregar workflow...</div>
            )}

            {!selectedAction && (
              <div className="text-center py-8 text-slate-500">
                Selecione uma ação de marketing para gerir o checklist de conformidade
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const SubContractorsSection = () => {
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);
  const [docsDialogOpen, setDocsDialogOpen] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [contractorDocs, setContractorDocs] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docForm, setDocForm] = useState({ name: '', description: '', document_type: 'contract', expiry_date: '' });
  const [form, setForm] = useState({
    name: '', nif: '', country: '', address: '',
    contact_name: '', contact_email: '', contact_phone: '',
    services_provided: '', data_processing_agreement: false, status: 'pending'
  });

  useEffect(() => {
    fetchContractors();
  }, []);

  const fetchContractors = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/compliance/sub-contractors`, { withCredentials: true });
      setContractors(response.data);
    } catch (error) {
      toast.error('Erro ao carregar sub-contratantes');
    } finally {
      setLoading(false);
    }
  };

  const fetchContractorDetails = async (contractorId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/compliance/sub-contractors/${contractorId}`, { withCredentials: true });
      setContractorDocs(response.data.documents || []);
    } catch (error) {
      toast.error('Erro ao carregar documentos');
    }
  };

  const resetForm = () => {
    setForm({
      name: '', nif: '', country: '', address: '',
      contact_name: '', contact_email: '', contact_phone: '',
      services_provided: '', data_processing_agreement: false, status: 'pending'
    });
    setEditingContractor(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingContractor) {
        await axios.put(`${BACKEND_URL}/api/compliance/sub-contractors/${editingContractor.sub_contractor_id}`, form, { withCredentials: true });
        toast.success('Sub-contratante atualizado!');
      } else {
        await axios.post(`${BACKEND_URL}/api/compliance/sub-contractors`, form, { withCredentials: true });
        toast.success('Sub-contratante criado!');
      }
      setDialogOpen(false);
      resetForm();
      fetchContractors();
    } catch (error) {
      toast.error('Erro ao guardar');
    }
  };

  const openEdit = (contractor) => {
    setEditingContractor(contractor);
    setForm({
      name: contractor.name,
      nif: contractor.nif || '',
      country: contractor.country || '',
      address: contractor.address || '',
      contact_name: contractor.contact_name || '',
      contact_email: contractor.contact_email || '',
      contact_phone: contractor.contact_phone || '',
      services_provided: contractor.services_provided || '',
      data_processing_agreement: contractor.data_processing_agreement || false,
      status: contractor.status
    });
    setDialogOpen(true);
  };

  const openDocsModal = (contractor) => {
    setSelectedContractor(contractor);
    fetchContractorDetails(contractor.sub_contractor_id);
    setDocsDialogOpen(true);
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedContractor) return;

    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', docForm.name || file.name);
    formData.append('description', docForm.description);
    formData.append('document_type', docForm.document_type);
    if (docForm.expiry_date) formData.append('expiry_date', docForm.expiry_date);

    try {
      await axios.post(
        `${BACKEND_URL}/api/compliance/sub-contractors/${selectedContractor.sub_contractor_id}/document`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Documento carregado!');
      setDocForm({ name: '', description: '', document_type: 'contract', expiry_date: '' });
      fetchContractorDetails(selectedContractor.sub_contractor_id);
      fetchContractors();
    } catch (error) {
      toast.error('Erro ao carregar documento');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleValidateDoc = async (docId, isValidated) => {
    try {
      await axios.put(`${BACKEND_URL}/api/compliance/sub-contractors/documents/${docId}/validate?is_validated=${isValidated}`, {}, { withCredentials: true });
      toast.success(isValidated ? 'Documento validado!' : 'Validação removida');
      fetchContractorDetails(selectedContractor.sub_contractor_id);
      fetchContractors();
    } catch (error) {
      toast.error('Erro ao validar documento');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este sub-contratante?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/compliance/sub-contractors/${id}`, { withCredentials: true });
      toast.success('Eliminado!');
      fetchContractors();
    } catch (error) {
      toast.error('Erro ao eliminar');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-amber-50 text-amber-700',
      approved: 'bg-emerald-50 text-emerald-700',
      rejected: 'bg-red-50 text-red-700',
      suspended: 'bg-slate-50 text-slate-700'
    };
    const labels = { pending: 'Pendente', approved: 'Aprovado', rejected: 'Rejeitado', suspended: 'Suspenso' };
    return <Badge className={colors[status]}>{labels[status]}</Badge>;
  };

  const getDocTypeBadge = (type) => {
    const colors = {
      contract: 'bg-blue-50 text-blue-700',
      dpa: 'bg-purple-50 text-purple-700',
      manual: 'bg-amber-50 text-amber-700',
      other: 'bg-slate-50 text-slate-700'
    };
    const labels = { contract: 'Contrato', dpa: 'DPA', manual: 'Manual', other: 'Outro' };
    return <Badge className={colors[type] || 'bg-slate-50'}>{labels[type] || type}</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-lg">Sub-Contratantes</h3>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="new-subcontractor-btn"><Plus className="w-4 h-4 mr-2" />Novo Sub-Contratante</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingContractor ? 'Editar Sub-Contratante' : 'Novo Sub-Contratante'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Nome da Entidade *</Label>
                  <Input data-testid="subcontractor-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
                </div>
                <div>
                  <Label>NIF</Label>
                  <Input value={form.nif} onChange={e => setForm({...form, nif: e.target.value})} />
                </div>
                <div>
                  <Label>País</Label>
                  <Input value={form.country} onChange={e => setForm({...form, country: e.target.value})} />
                </div>
                <div className="col-span-2">
                  <Label>Morada</Label>
                  <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
                </div>
                <div>
                  <Label>Nome do Contacto</Label>
                  <Input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} />
                </div>
                <div>
                  <Label>Telefone</Label>
                  <Input value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} />
                </div>
                <div>
                  <Label>Estado</Label>
                  <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pendente</SelectItem>
                      <SelectItem value="approved">Aprovado</SelectItem>
                      <SelectItem value="rejected">Rejeitado</SelectItem>
                      <SelectItem value="suspended">Suspenso</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Serviços Prestados</Label>
                  <Textarea value={form.services_provided} onChange={e => setForm({...form, services_provided: e.target.value})} />
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <Switch checked={form.data_processing_agreement} onCheckedChange={v => setForm({...form, data_processing_agreement: v})} />
                  <Label>Acordo de Tratamento de Dados (DPA) assinado</Label>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancelar</Button>
                <Button type="submit" data-testid="save-subcontractor-btn">Guardar</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Documents Dialog */}
      <Dialog open={docsDialogOpen} onOpenChange={setDocsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Documentos - {selectedContractor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Upload Form */}
            <Card className="border-dashed">
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nome do Documento</Label>
                    <Input value={docForm.name} onChange={e => setDocForm({...docForm, name: e.target.value})} placeholder="Ex: Contrato de Serviços" />
                  </div>
                  <div>
                    <Label>Tipo</Label>
                    <Select value={docForm.document_type} onValueChange={v => setDocForm({...docForm, document_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="contract">Contrato</SelectItem>
                        <SelectItem value="dpa">DPA</SelectItem>
                        <SelectItem value="manual">Manual</SelectItem>
                        <SelectItem value="other">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Descrição</Label>
                    <Input value={docForm.description} onChange={e => setDocForm({...docForm, description: e.target.value})} />
                  </div>
                  <div>
                    <Label>Data de Expiração</Label>
                    <Input type="date" value={docForm.expiry_date} onChange={e => setDocForm({...docForm, expiry_date: e.target.value})} />
                  </div>
                </div>
                <div className="mt-4">
                  <Label htmlFor="doc-upload" className="cursor-pointer">
                    <div className="flex items-center justify-center p-4 border-2 border-dashed rounded-lg hover:bg-slate-50">
                      <Upload className="w-5 h-5 mr-2 text-slate-500" />
                      <span className="text-sm text-slate-600">{uploadingDoc ? 'A carregar...' : 'Clique para carregar ficheiro'}</span>
                    </div>
                  </Label>
                  <input id="doc-upload" type="file" className="hidden" onChange={handleDocUpload} disabled={uploadingDoc} />
                </div>
              </CardContent>
            </Card>

            {/* Documents List */}
            <div className="space-y-2">
              {contractorDocs.length === 0 ? (
                <div className="text-center py-4 text-slate-500">Nenhum documento</div>
              ) : (
                contractorDocs.map(doc => (
                  <div key={doc.document_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-slate-500" />
                      <div>
                        <div className="font-medium text-[#0F172A]">{doc.name}</div>
                        <div className="flex items-center gap-2 mt-1">
                          {getDocTypeBadge(doc.document_type)}
                          {doc.is_validated && <Badge className="bg-emerald-50 text-emerald-700">Validado</Badge>}
                          {doc.expiry_date && (
                            <span className="text-xs text-slate-500">
                              Expira: {new Date(doc.expiry_date).toLocaleDateString('pt-PT')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant={doc.is_validated ? 'outline' : 'default'}
                        size="sm"
                        onClick={() => handleValidateDoc(doc.document_id, !doc.is_validated)}
                        className={doc.is_validated ? '' : 'bg-emerald-600 hover:bg-emerald-700'}
                      >
                        {doc.is_validated ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="text-center py-8 text-slate-500">A carregar...</div>
      ) : contractors.length === 0 ? (
        <div className="text-center py-8 text-slate-500">Nenhum sub-contratante registado</div>
      ) : (
        <div className="space-y-3">
          {contractors.map(contractor => {
            const hasDpaDoc = contractor.dpa_documents_count > 0;
            const needsDpaDoc = contractor.data_processing_agreement && !hasDpaDoc;
            
            return (
              <Card key={contractor.sub_contractor_id} className={`border-slate-200 ${needsDpaDoc ? 'border-amber-300 bg-amber-50/30' : ''}`} data-testid={`subcontractor-${contractor.sub_contractor_id}`}>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="font-semibold text-[#0F172A]">{contractor.name}</h4>
                        {getStatusBadge(contractor.status)}
                        {contractor.data_processing_agreement && (
                          <Badge className={hasDpaDoc ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}>
                            {hasDpaDoc ? '✓ DPA Anexado' : '⚠ DPA Pendente'}
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-slate-600">
                        {contractor.country && <span>{contractor.country} • </span>}
                        {contractor.nif && <span>NIF: {contractor.nif} • </span>}
                        {contractor.contact_email && <span>{contractor.contact_email}</span>}
                      </div>
                      {contractor.services_provided && (
                        <div className="text-xs text-slate-500 mt-1">
                          Serviços: {contractor.services_provided.substring(0, 100)}{contractor.services_provided.length > 100 ? '...' : ''}
                        </div>
                      )}
                      <div className="text-xs text-slate-500 mt-1">
                        Documentos: {contractor.validated_documents || 0}/{contractor.total_documents || 0} validados
                      </div>
                      {needsDpaDoc && (
                        <div className="mt-2 p-2 bg-amber-100 rounded-lg flex items-center gap-2 text-amber-800 text-sm">
                          <AlertCircle className="w-4 h-4" />
                          <span>DPA marcado como assinado mas sem documento anexado.</span>
                          <Button 
                            variant="link" 
                            size="sm" 
                            className="text-amber-800 underline p-0 h-auto"
                            onClick={() => { setDocForm({...docForm, document_type: 'dpa'}); openDocsModal(contractor); }}
                          >
                            Anexar DPA
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 ml-2">
                      <Button variant="outline" size="sm" onClick={() => openDocsModal(contractor)}>
                        <FileText className="w-4 h-4 mr-1" />Docs
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openEdit(contractor)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => handleDelete(contractor.sub_contractor_id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SCCTemplatesSection = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', jurisdiction: 'EU',
    template_type: 'controller_to_processor', content: '', is_active: true
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/compliance/scc-templates`, { withCredentials: true });
      setTemplates(response.data);
    } catch (error) {
      toast.error('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/compliance/scc-templates`, form, { withCredentials: true });
      toast.success('Template criado!');
      setDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      toast.error('Erro ao guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este template?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/compliance/scc-templates/${id}`, { withCredentials: true });
      toast.success('Eliminado!');
      fetchTemplates();
    } catch (error) {
      toast.error('Erro ao eliminar');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-lg">Templates SCC (Standard Contractual Clauses)</h3>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="w-4 h-4 mr-2" />Novo Template</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Novo Template SCC</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Nome *</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
              </div>
              <div>
                <Label>Descrição</Label>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Jurisdição</Label>
                  <Select value={form.jurisdiction} onValueChange={v => setForm({...form, jurisdiction: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EU">União Europeia</SelectItem>
                      <SelectItem value="UK">Reino Unido</SelectItem>
                      <SelectItem value="US">Estados Unidos</SelectItem>
                      <SelectItem value="Other">Outra</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tipo</Label>
                  <Select value={form.template_type} onValueChange={v => setForm({...form, template_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="controller_to_processor">Responsável → Subcontratante</SelectItem>
                      <SelectItem value="controller_to_controller">Responsável → Responsável</SelectItem>
                      <SelectItem value="processor_to_processor">Subcontratante → Subcontratante</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Conteúdo do Template</Label>
                <Textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} rows={6} placeholder="Cláusulas contratuais..." />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                <Button type="submit">Guardar</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-500">A carregar...</div>
      ) : templates.length === 0 ? (
        <div className="text-center py-8 text-slate-500">Nenhum template SCC definido</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templates.map(template => (
            <Card key={template.template_id} className="border-slate-200">
              <CardContent className="pt-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-[#0F172A]">{template.name}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline">{template.jurisdiction}</Badge>
                      <Badge variant="outline" className="text-xs">{template.template_type.replace(/_/g, ' ')}</Badge>
                    </div>
                    {template.description && <p className="text-sm text-slate-600 mt-2">{template.description}</p>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(template.template_id)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const DocumentRepositorySection = () => {
  const [categories, setCategories] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
  const [documentForm, setDocumentForm] = useState({ name: '', description: '', category_id: '', document_type: 'link', external_link: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [catRes, docRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/compliance/document-categories`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/compliance/documents`, { withCredentials: true })
      ]);
      setCategories(catRes.data);
      setDocuments(docRes.data);
    } catch (error) {
      toast.error('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/compliance/document-categories`, categoryForm, { withCredentials: true });
      toast.success('Categoria criada!');
      setCategoryDialogOpen(false);
      setCategoryForm({ name: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar categoria');
    }
  };

  const handleCreateDocument = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/compliance/documents`, documentForm, { withCredentials: true });
      toast.success('Documento criado!');
      setDocumentDialogOpen(false);
      setDocumentForm({ name: '', description: '', category_id: '', document_type: 'link', external_link: '' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar documento');
    }
  };

  const handleDeleteDocument = async (id) => {
    if (!window.confirm('Eliminar este documento?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/compliance/documents/${id}`, { withCredentials: true });
      toast.success('Eliminado!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao eliminar');
    }
  };

  const getDocumentsByCategory = (categoryId) => {
    return documents.filter(d => d.category_id === categoryId);
  };

  const uncategorizedDocs = documents.filter(d => !d.category_id);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-lg">Repositório Documental</h3>
        <div className="flex gap-2">
          <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline"><FolderOpen className="w-4 h-4 mr-2" />Nova Categoria</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nova Categoria</DialogTitle></DialogHeader>
              <form onSubmit={handleCreateCategory} className="space-y-4">
                <div>
                  <Label>Nome *</Label>
                  <Input value={categoryForm.name} onChange={e => setCategoryForm({...categoryForm, name: e.target.value})} required />
                </div>
                <div>
                  <Label>Descrição</Label>
                  <Textarea value={categoryForm.description} onChange={e => setCategoryForm({...categoryForm, description: e.target.value})} />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit">Guardar</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={documentDialogOpen} onOpenChange={setDocumentDialogOpen}>
            <DialogTrigger asChild>
              <Button><Plus className="w-4 h-4 mr-2" />Novo Documento</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Novo Documento</DialogTitle></DialogHeader>
              <form onSubmit={handleCreateDocument} className="space-y-4">
                <div>
                  <Label>Nome *</Label>
                  <Input value={documentForm.name} onChange={e => setDocumentForm({...documentForm, name: e.target.value})} required />
                </div>
                <div>
                  <Label>Descrição</Label>
                  <Textarea value={documentForm.description} onChange={e => setDocumentForm({...documentForm, description: e.target.value})} />
                </div>
                <div>
                  <Label>Categoria</Label>
                  <Select value={documentForm.category_id || 'none'} onValueChange={v => setDocumentForm({...documentForm, category_id: v === 'none' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Selecionar categoria" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sem categoria</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Link Externo (SharePoint, Google Drive, etc.)</Label>
                  <Input value={documentForm.external_link} onChange={e => setDocumentForm({...documentForm, external_link: e.target.value})} placeholder="https://..." />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDocumentDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit">Guardar</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-500">A carregar...</div>
      ) : (
        <div className="space-y-4">
          {categories.map(category => (
            <Collapsible key={category.category_id} defaultOpen>
              <CollapsibleTrigger className="flex items-center gap-2 w-full p-3 bg-slate-100 rounded-lg hover:bg-slate-200">
                <FolderOpen className="w-5 h-5 text-blue-600" />
                <span className="font-medium">{category.name}</span>
                <Badge variant="outline" className="ml-2">{getDocumentsByCategory(category.category_id).length}</Badge>
                <ChevronDown className="w-4 h-4 ml-auto" />
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 pl-6 space-y-2">
                {getDocumentsByCategory(category.category_id).length === 0 ? (
                  <p className="text-sm text-slate-500 py-2">Nenhum documento nesta categoria</p>
                ) : (
                  getDocumentsByCategory(category.category_id).map(doc => (
                    <div key={doc.document_id} className="flex items-center justify-between p-2 bg-white border rounded">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-500" />
                        <span className="text-sm">{doc.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {doc.external_link && (
                          <a href={doc.external_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteDocument(doc.document_id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </CollapsibleContent>
            </Collapsible>
          ))}

          {uncategorizedDocs.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-slate-700">Sem Categoria</h4>
              {uncategorizedDocs.map(doc => (
                <div key={doc.document_id} className="flex items-center justify-between p-2 bg-white border rounded">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-500" />
                    <span className="text-sm">{doc.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.external_link && (
                      <a href={doc.external_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteDocument(doc.document_id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {categories.length === 0 && documents.length === 0 && (
            <div className="text-center py-8 text-slate-500">Nenhuma categoria ou documento registado</div>
          )}
        </div>
      )}
    </div>
  );
};

const CompliancePage = () => {
  const [expandedSection, setExpandedSection] = useState('dpia');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Gestão de Compliance
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          DPIA, Checklists, Sub-Contratantes, SCC Templates e Repositório Documental
        </p>
      </div>

      <div className="space-y-4">
        {/* DPIA Section */}
        <Collapsible open={expandedSection === 'dpia'} onOpenChange={() => setExpandedSection(expandedSection === 'dpia' ? '' : 'dpia')}>
          <CollapsibleTrigger className="flex items-center justify-between w-full p-4 bg-white border rounded-lg hover:bg-slate-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg"><Shield className="w-5 h-5 text-blue-600" /></div>
              <span className="font-semibold text-[#0F172A]">DPIA - Avaliação de Impacto</span>
            </div>
            {expandedSection === 'dpia' ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 p-4 bg-white border rounded-lg">
            <DPIASection />
          </CollapsibleContent>
        </Collapsible>

        {/* Checklist Section */}
        <Collapsible open={expandedSection === 'checklist'} onOpenChange={() => setExpandedSection(expandedSection === 'checklist' ? '' : 'checklist')}>
          <CollapsibleTrigger className="flex items-center justify-between w-full p-4 bg-white border rounded-lg hover:bg-slate-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg"><CheckCircle className="w-5 h-5 text-emerald-600" /></div>
              <span className="font-semibold text-[#0F172A]">Checklist de Conformidade</span>
            </div>
            {expandedSection === 'checklist' ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 p-4 bg-white border rounded-lg">
            <ChecklistSection />
          </CollapsibleContent>
        </Collapsible>

        {/* Sub-Contractors Section */}
        <Collapsible open={expandedSection === 'subcontractors'} onOpenChange={() => setExpandedSection(expandedSection === 'subcontractors' ? '' : 'subcontractors')}>
          <CollapsibleTrigger className="flex items-center justify-between w-full p-4 bg-white border rounded-lg hover:bg-slate-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg"><Users className="w-5 h-5 text-purple-600" /></div>
              <span className="font-semibold text-[#0F172A]">Sub-Contratantes</span>
            </div>
            {expandedSection === 'subcontractors' ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 p-4 bg-white border rounded-lg">
            <SubContractorsSection />
          </CollapsibleContent>
        </Collapsible>

        {/* SCC Templates Section */}
        <Collapsible open={expandedSection === 'scc'} onOpenChange={() => setExpandedSection(expandedSection === 'scc' ? '' : 'scc')}>
          <CollapsibleTrigger className="flex items-center justify-between w-full p-4 bg-white border rounded-lg hover:bg-slate-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg"><FileCheck className="w-5 h-5 text-amber-600" /></div>
              <span className="font-semibold text-[#0F172A]">Templates SCC</span>
            </div>
            {expandedSection === 'scc' ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 p-4 bg-white border rounded-lg">
            <SCCTemplatesSection />
          </CollapsibleContent>
        </Collapsible>

        {/* Document Repository Section */}
        <Collapsible open={expandedSection === 'documents'} onOpenChange={() => setExpandedSection(expandedSection === 'documents' ? '' : 'documents')}>
          <CollapsibleTrigger className="flex items-center justify-between w-full p-4 bg-white border rounded-lg hover:bg-slate-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg"><FolderOpen className="w-5 h-5 text-slate-600" /></div>
              <span className="font-semibold text-[#0F172A]">Repositório Documental</span>
            </div>
            {expandedSection === 'documents' ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 p-4 bg-white border rounded-lg">
            <DocumentRepositorySection />
          </CollapsibleContent>
        </Collapsible>
      </div>
    </div>
  );
};

export default CompliancePage;
